import { useState, type FormEvent } from "react";
import { apiPostAuth, isApiConfigured, ApiError } from "@/services/http";
import { useAuth } from "@/contexts/AuthContext";
import type { Plan } from "@/types/plans";

// ── Types ─────────────────────────────────────────────────────────────────────

/** Plan info shown in the upgrade dialog (full plan, incl. highlights). */
export type PlanInfo = Plan;

interface UpgradeModalProps {
  plan:    Plan;
  onClose: () => void;
}

interface OrderPayload {
  planSlug:     string;
  billingCycle: "yearly";
  contactName:  string;
  contactEmail: string;
  businessName: string;
  notes:        string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function UpgradeModal({ plan, onClose }: UpgradeModalProps) {
  const { user, accessToken, isDemoMode } = useAuth();

  const [contactName,  setContactName]  = useState(user?.name ?? "");
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [businessName, setBusinessName] = useState("");
  const [notes,        setNotes]        = useState("");
  const [submitting,   setSubmitting]   = useState(false);
  const [success,      setSuccess]      = useState(false);
  const [errorMsg,     setErrorMsg]     = useState("");

  const isFree = plan.priceYearly === 0;
  const price  = plan.priceYearly;
  const planHighlights = plan.highlights;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");

    const payload: OrderPayload = {
      planSlug:     plan.slug,
      billingCycle: "yearly",
      contactName:  contactName.trim(),
      contactEmail: contactEmail.trim(),
      businessName: businessName.trim(),
      notes:        notes.trim(),
    };

    try {
      if (isDemoMode || !isApiConfigured() || !accessToken) {
        // Demo mode — simulate processing
        await new Promise((r) => setTimeout(r, 900));
      } else {
        await apiPostAuth<{ id: number }>("/orders", payload, accessToken);
      }
      setSuccess(true);
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError ? err.message : "Could not submit the upgrade request. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "h-11 w-full rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal card */}
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${plan.icon} text-base`} />
            </span>
            <div>
              <p className="text-sm font-bold text-foreground-950">Upgrade to {plan.name}</p>
              <p className="text-xs text-foreground-500">
                {isFree ? "Always free" : `₹${price.toLocaleString("en-IN")} / year`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Success state */}
        {success ? (
          <div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <i className="ri-checkbox-circle-fill text-3xl text-emerald-600" />
            </div>
            <div>
              <p className="font-heading text-lg font-bold text-foreground-950">
                {isFree ? "Free Plan Activated!" : "Upgrade Request Received!"}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {isFree
                  ? "You are on the Free plan. Start adding your business listing right away."
                  : "Our team will contact you within 24 hours to complete the upgrade and set up payment for your Coastal Bizz Connect listing."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-6 py-5">
            {/* Plan summary */}
            {!isFree && (
              <div className="flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-700">
                  {plan.name} · billed yearly
                </span>
                <span className="font-heading text-base font-bold text-primary-700">
                  ₹{price.toLocaleString("en-IN")}
                </span>
              </div>
            )}

            {/* What's included */}
            {planHighlights.length > 0 && (
              <div className="rounded-lg border border-background-200 bg-background-100/60 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                  What&apos;s included in {plan.name}
                </p>
                <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {planHighlights.slice(0, 6).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-foreground-700">
                      <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-secondary-600">
                        <i className="ri-check-line text-sm" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                {planHighlights.length > 6 && (
                  <p className="mt-2 text-[11px] text-foreground-400">
                    +{planHighlights.length - 6} more included
                  </p>
                )}
              </div>
            )}

            {/* Contact details */}
            <div>
              <label htmlFor="up-name" className={labelClass}>Your name</label>
              <input
                id="up-name"
                type="text"
                required
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Full name"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="up-email" className={labelClass}>Email address</label>
              <input
                id="up-email"
                type="email"
                required
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="your@email.com"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="up-business" className={labelClass}>Business name</label>
              <input
                id="up-business"
                type="text"
                required
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Your registered business name"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="up-notes" className={labelClass}>Additional notes <span className="normal-case text-foreground-400">(optional)</span></label>
              <textarea
                id="up-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any questions or specific requirements…"
                className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            {isDemoMode && (
              <div className="rounded-lg bg-accent-50 px-4 py-2.5 text-xs text-accent-700">
                <i className="ri-information-line mr-1" />
                Demo mode — this form simulates a successful submission without sending data.
              </div>
            )}

            {errorMsg && (
              <div className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <i className="ri-error-warning-line mr-2" />{errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin text-base" /> Processing…</>
              ) : isFree ? (
                <><i className="ri-store-2-line text-base" /> Start with Free Plan</>
              ) : (
                <><i className="ri-rocket-2-line text-base" /> Request {plan.name} Upgrade — ₹{price.toLocaleString("en-IN")}</>
              )}
            </button>

            <p className="text-center text-[11px] text-foreground-400">
              By submitting, our team will confirm availability and payment details within 24 hours.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}